import { inPausa } from './pausa.ts';
/**
 * IL TEMPO CHE RESTA, nelle partite a tempo — e il modo di DIRE che non ce n'e'.
 *
 * [Luca, 2026-09-14: «Nelle partite a tempo indica il tempo rimanente (e altre
 * informazioni relative quando esistono e necessarie).»]
 *
 * ═══ 🔴 LA FORMA VERA DEL DATO — E NON «VARIA» PIU' ═══
 * `projection.ts` porta da mesi «*Clock omitted in v1 (state.clock shape
 * varies; add later)*». Rimisurato il 14/09, quella riga e' **stantia su due
 * fronti**:
 *   · il campo non si chiama `clock` ma **`clocks`**, al plurale, e non e' «la
 *     forma del lato di turno»: sono **i due lati insieme**;
 *   · la forma **e' dichiarata**, oggi, nei tipi installati.
 *
 * [M 2026-09-14, letto in
 *  `node_modules/@qlashgeminiz/qgammon-board-ui/node_modules/@qlashgeminiz/qgammon-types/dist/events.d.ts:659-688`
 *  (il blocco `clocks` dentro `MatchStatePayloadSchema`) e `…/dist/dto.d.ts:2-45`
 *  (`TimeControlSchema` + `hasNoClock`)]:
 *
 *     clocks: { W: number; B: number; unlimited?: boolean }   // W/B in MILLISECONDI
 *
 * ⚠️ **E la copia che conta e' quella ANNIDATA.** In `node_modules` ci sono DUE
 * `@qlashgeminiz/qgammon-types`: quella di primo livello e' **0.2.0** e non
 * conosce ne' `unlimited` ne' `status`; quella sotto `qgammon-board-ui@0.4.3` e'
 * **0.11.0** e li dichiara entrambi. `BackgammonSocket.state` risolve sulla
 * SECONDA — quindi `state.clocks.unlimited` e' tipato **nell'app**, mentre un
 * `import ... from '@qlashgeminiz/qgammon-types'` scritto qui dentro leggerebbe
 * la prima e non lo troverebbe. *Due versioni dello stesso pacchetto nello
 * stesso albero non danno nessun errore: danno due verita'.*
 * ⇒ Provato con una sonda `@ts-expect-error` su `state.clocks.unlimited`: `tsc`
 * risponde **TS2578 «Unused '@ts-expect-error' directive»**, cioe' l'accesso e'
 * valido. (E lo stesso vale per `status`: la nota di `matchStatus.ts` «0.2.0 non
 * dichiara `status`» e' vera della copia sbagliata.)
 *
 * 📌 ANNOTAZIONE 29/09 (aggiornamento a `qgammon-types@0.16.0` / `board-ui@0.11.0`): la copia di primo
 * livello non e' piu' 0.2.0 ma 0.16.0, e la copia ANNIDATA sotto `board-ui` non esiste piu' (npm ha
 * deduplicato: `board-ui@0.11.0` dipende da types `0.16.0`, la stessa). Le due verita' sono diventate
 * una. Quanto sopra descrive lo stato del 14/09 e resta come misura di quel giorno, non come stato di oggi.
 *
 * ═══ 🔴 PERCHE' `null` E NON `0:00` — ED E' MISURATO, NON UNA CAUTELA ═══
 * Una partita **senza orologio** manda `clocks: {W: 0, B: 0, unlimited: true}`,
 * e i due zeri ci sono **dal primo snapshot**, non a tempo scaduto:
 *   · `PRESET_TIME_CONTROLS.unlimited = {bankSeconds: 0, incrementSeconds: 0,
 *     unlimited: true}` [M, `…/qgammon-types/dist/dto.js:55`];
 *   · `MatchSession` costruisce il `ClockManager` con quella banca — `bankSeconds:
 *     Math.round(tc.bankSeconds * Math.sqrt(matchLength))`, e «la scala ×√N resta e
 *     produce 0 da 0» [M `QGammon@origin/main` `8ac8238`, `game/match.ts:181-189`];
 *   · `remaining()` risponde `Math.max(0, banked - elapsed)` ⇒ **0, sempre**.
 * ⇒ ***Un client che ignora `unlimited` disegna `00:00 / 00:00` a una partita
 * appena cominciata.*** Il server lo dice in chiaro nel proprio sorgente: «Due
 * zeri da soli si leggono "tempo scaduto" — cioe' il contrario di quello che e'
 * successo — e un client onesto non ha modo di distinguerli»
 * [`game/payloads.ts:77-85`].
 * ⛔ Quindi `null`, mai uno zero: *uno zero plausibile su un orologio non e' un
 * dato mancante, e' un'AFFERMAZIONE sbagliata.*
 *
 * ═══ 🔑 E `null` DA SOLO NON BASTA: I CASI SONO DUE ═══
 * «Questa partita non ha orologio» e «non ho il dato» sono due stati diversi e
 * due frasi diverse — la prima si dice, la seconda no. E' la stessa regola dei
 * pannelli della Hub (`listVerdict`), dove UN booleano che ne diceva due ha
 * prodotto vuoti affermati su domande mai poste.
 * ⇒ `verdettoOrologio` li separa (`'senza'` / `'ignoto'`), e `orologioDa` e' la
 * comodita' `| null` per chi deve solo decidere se disegnare.
 *
 * ═══ ⚠️ COSA IL FILO NON PORTA, e che quindi qui NON si inventa ═══
 * [M, stesso ref, `game/payloads.ts:66-92` — cioe' l'unico punto che costruisce
 * lo snapshot]
 *   · **l'incremento Fischer e la banca iniziale**: `timeControl` NON e' in
 *     `MatchStatePayload` (e nemmeno in `MatchmakingPairedPayload`) ⇒ niente
 *     «+10s a mossa», niente barra di percentuale: la banca di partenza e'
 *     scalata ×√N e non e' ricostruibile da qui;
 *   · **`owner` e `running`**: il `ClockManager` li calcola
 *     (`clocks.ts:snapshot()`) e il payload **li butta via** ⇒ quale lato stia
 *     scorrendo lo si DEDUCE (v. `latoChePensa`), non lo si legge;
 *   · **l'istante della misura**: nessun timestamp accompagna i due numeri.
 *
 * ═══ 🔴 IL TEMPO SCORRE, E QUESTO MODULO RESTA PURO ═══
 * Fra due `match:state` possono passare minuti (uno snapshot per ply): senza
 * estrapolazione l'orologio resta **fermo mentre l'avversario pensa** — la
 * stessa classe della banda della Hub, che scadeva a schermo perche' l'effetto
 * che la calcolava non dipendeva dal passare del tempo.
 * ⇒ Si estrapola, ma i due istanti **arrivano dal chiamante** (`ricevutoA`,
 * `adesso`): nessun `Date.now()` qui dentro, o il banco non potrebbe eseguirlo.
 *
 * ⚠️ **I DUE ERRORI DELL'ESTRAPOLAZIONE, dichiarati — chi integra li deve sapere.**
 *  1. `ricevutoA` e' l'istante dell'ARRIVO, non quello della misura: la latenza
 *     di rete ci fa mostrare **un filo PIU' tempo** di quello vero. E' il verso
 *     giusto in cui sbagliare — a dichiarare la scadenza e' il server, e un
 *     client che mostrasse MENO annuncerebbe una sconfitta non avvenuta.
 *  2. ⛔ **Una disconnessione mette in pausa l'orologio sul server e noi non lo
 *     vediamo**: `session.ts:358` fa `clocks.pause()` quando un lato cade, e il
 *     payload non porta ne' `running` ne' la presenza. ⇒ in quel caso scaliamo
 *     un tempo che sul server e' fermo, e sbagliamo **per difetto**. Per questo
 *     `aZero` dice «il numero che mostro e' zero» e **non** «ha perso a tempo»:
 *     quella frase la pronuncia solo `match:ended`.
 */

/** I due lati, come li nomina il protocollo QGammon. */
export type LatoQgammon = 'W' | 'B';

/** Un lato dell'orologio, gia' pronto da mostrare. */
export interface LatoOrologio {
  /** Millisecondi rimasti a `adesso`. **Mai negativo.** */
  ms: number;
  /** `ms` gia' formattato — v. `formattaTempo`. */
  testo: string;
  /**
   * Sotto il minuto ⇒ `testo` e' la forma corta (solo secondi, senza il campo
   * dei minuti) e la UI ci appende l'unita' con `match.orologio.corto`.
   */
  corto: boolean;
  /** Stiamo scalando questo lato? (⇒ `ms` e' una stima, v. il blocco in testa.) */
  scorre: boolean;
  /**
   * `ms === 0`. ⛔ Significa **«il numero che mostro e' zero»**, non «ha perso a
   * tempo»: la sconfitta la dichiara il server con `match:ended`, e noi non
   * vediamo le pause (disconnessione). Serve a colorare, non a scrivere.
   */
  aZero: boolean;
}

/** I due orologi di una partita a tempo. */
export interface Orologio {
  W: LatoOrologio;
  B: LatoOrologio;
  /** Il lato che crediamo stia scalando, o `null` se non sta scalando nessuno. */
  scorre: LatoQgammon | null;
}

/**
 * Le TRE risposte possibili, perche' due non bastano.
 *
 * ⛔ `'senza'` e `'ignoto'` non si collassano: la prima e' una cosa da DIRE
 * («senza orologio», `match.orologio.senza`), la seconda e' una cosa da tacere
 * finche' non si sa. Collassarle rifa' il vuoto affermato su una domanda mai
 * posta.
 */
export type VerdettoOrologio =
  | { tipo: 'orologio'; orologio: Orologio }
  | { tipo: 'senza' }
  | { tipo: 'ignoto' };

/** I due istanti, che il chiamante passa: questo modulo non legge l'orologio di sistema. */
export interface Istanti {
  /** Quando e' arrivato lo snapshot che porta questi numeri (epoch ms). */
  ricevutoA: number;
  /** Adesso (epoch ms). */
  adesso: number;
}

const SECONDI_PER_MINUTO = 60;
const MINUTI_PER_ORA = 60;

/**
 * I secondi che si MOSTRANO, arrotondati per ECCESSO.
 *
 * 🔑 `ceil` e non `floor`, ed e' la stessa regola dello zero: con `floor` un
 * orologio a 900 ms scrive `0` per quasi un secondo intero a chi il tempo ce
 * l'ha ancora — cioe' mostra la scadenza prima che sia successa. Con `ceil`
 * lo zero compare **solo** quando non resta davvero niente.
 *
 * ⛔ **E' l'UNICO posto in cui si arrotonda.** `formattaTempo` e `sottoIlMinuto`
 * devono decidere sullo STESSO numero: a 59 999 ms il tempo arrotondato e' 60 s,
 * e due funzioni che arrotondassero per conto proprio scriverebbero `1:00`
 * dicendo «e' sotto il minuto» — il bordo esatto in cui nessuno guarda.
 */
export function secondiDi(ms: number): number {
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / 1000);
}

/** Il tempo sta per finire ⇒ la UI usa la forma corta (`match.orologio.corto`). */
export function sottoIlMinuto(ms: number): boolean {
  return secondiDi(ms) < SECONDI_PER_MINUTO;
}

/**
 * `mm:ss` — `h:mm:ss` sopra l'ora, e i soli secondi sotto il minuto.
 *
 * ⛔ **Un tempo NEGATIVO non si stampa: si tosa a zero.** Il server non ne manda
 * (`remaining()` fa gia' `Math.max(0, …)`), ma **l'estrapolazione locale ci
 * arriva da sola** appena `adesso - ricevutoA` supera il rimasto — cioe' nel
 * caso normale in cui nessuno tocca lo schermo per un minuto. Senza la tosatura
 * uscirebbe `-1:-30`, che non e' un tempo ed e' il modo in cui un difetto di
 * calcolo si traveste da orologio.
 *
 * ⚠️ E' **totale** di proposito — un `NaN` esce `0`, non `NaN:NaN`. La regola di
 * onesta' («mai uno zero inventato») non vive qui: vive a monte, in
 * `verdettoOrologio`, che risponde `null`/`'senza'`/`'ignoto'` invece di
 * chiamare questa funzione. *Un formattatore che sa anche dire «non lo so» e'
 * un formattatore che verra' usato per deciderlo.*
 */
export function formattaTempo(ms: number): string {
  const totale = secondiDi(ms);
  const secondi = totale % SECONDI_PER_MINUTO;
  const minutiTotali = Math.floor(totale / SECONDI_PER_MINUTO);
  if (minutiTotali === 0) return String(secondi);
  const ss = String(secondi).padStart(2, '0');
  if (minutiTotali < MINUTI_PER_ORA) return `${minutiTotali}:${ss}`;
  const ore = Math.floor(minutiTotali / MINUTI_PER_ORA);
  const mm = String(minutiTotali % MINUTI_PER_ORA).padStart(2, '0');
  return `${ore}:${mm}:${ss}`;
}

/** L'altro lato. */
export function altroLato(lato: LatoQgammon): LatoQgammon {
  return lato === 'W' ? 'B' : 'W';
}

function oggetto(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

/**
 * QUALE LATO STA CONSUMANDO IL PROPRIO TEMPO — dedotto, perche' il filo non lo dice.
 *
 * [M `QGammon@origin/main` `8ac8238`, tutte le chiamate a `clocks.start/pause`]
 * L'orologio segue `game.turn`, **con un'eccezione e quattro pause**:
 *   · ⭐ **cubo offerto**: `offerCube` fa `clocks.start(receiver)` con il commento
 *     «Decision time runs on the receiver's clock» (`session.ts:450-458`), mentre
 *     `game.turn` resta all'offerente — `respondCube` rifiuta chi ha il turno.
 *     ⇒ in `phase: 'cubeOffered'` **scorre l'ALTRO**, ed e' l'unico punto in cui
 *     turno e orologio si separano. Mostrare il turno li' e' scalare il tempo
 *     alla persona sbagliata, mentre l'altra decide;
 *   · `status: 'waiting'` — un APPUNTAMENTO (ADR-0114): «no clock, no grace
 *     budget, nothing to lose», e infatti il clock non parte finche' non ci sono
 *     entrambi (`session.ts:349-356`);
 *   · `betweenGames` e `phase: 'gameOver'` — `settleCurrentGame` fa `pause()`
 *     (`match.ts:363`);
 *   · **la disconnessione**, che NON vediamo: dichiarata nel blocco in testa.
 *
 * `null` = «non sta scalando nessuno», e vale anche per uno snapshot che non
 * riconosco: preferire un lato a caso sarebbe scalare il tempo a qualcuno per
 * un'ipotesi.
 */
export function latoChePensa(state: unknown): LatoQgammon | null {
  const s = oggetto(state);
  if (!s) return null;
  if (s.status === 'waiting') return null;
  if (s.betweenGames === true) return null;
  // ⭐ PAUSA CONCORDATA (ADR-0176, QGammon #97): accettata, ferma gli orologi di ENTRAMBI sul server
  //    («In pausa fino alle HH:MM con gli orologi fermi», L-2410). Una PROPOSTA no: fino al «si'»
  //    l'orologio di chi ha il turno corre. Qui, e non a valle, perche' questo e' il predicato che
  //    fa scendere l'orologio a schermo E accende i due banner (`segnaliDelTurno`): uno solo.
  //    Stesso predicato di `inPausa` (`lib/qgammon/pausa.ts`) che spegne «Tira»: due predicati diversi
  //    davano orologi fermi e «Tira» vivo su uno snapshot malformato (revisione di #493).
  if (inPausa(state)) return null;
  const game = oggetto(s.game);
  if (!game) return null;
  const turno = game.turn;
  if (turno !== 'W' && turno !== 'B') return null;
  if (game.phase === 'gameOver') return null;
  if (game.phase === 'cubeOffered') return altroLato(turno);
  return turno;
}

/**
 * LA DOMANDA «quanto tempo resta?», con le sue tre risposte.
 *
 * `state` e' `unknown` di proposito, e la ragione qui e' **diversa** da quella di
 * `matchStatus.ts` (che lo fa perche' i tipi non dichiarano il campo): qui il
 * campo e' dichiarato eccome — quello che non e' dichiarato e' che il *filo*
 * rispetti il tipo. Un payload di socket non e' un oggetto tipato finche'
 * qualcuno non l'ha guardato: `board-ui` fa `socket.on(State, (p) => setState(p))`
 * **senza nessun parse zod** (`qgammon-board-ui@0.4.3`, `dist/index.js:1275-1277`;
 * zero occorrenze di `parse`/`safeParse` nel bundle). ⇒ il controllo a runtime
 * e' l'unico che esiste, e sta qui.
 *
 * ⛔ `unlimited` si confronta con `=== true`, mai per verita' generica — e' la
 * postura che i tipi stessi prescrivono (`hasNoClock`, `dto.d.ts:36-46`): il
 * campo e' opzionale, e **il server lo emette SOLO quando e' vero**
 * (`...(match.clocks.isUnlimited() && { unlimited: true as const })`,
 * `payloads.ts:84`; il suo test pretende `"unlimited" in p.clocks === false`
 * sulle partite con orologio). Assente ⇒ **con orologio**, che e' anche il
 * comportamento giusto contro un gameserver piu' vecchio del campo. E `false`
 * esplicito vuol dire «con orologio» come l'assenza, non «non lo so».
 */
export function verdettoOrologio(state: unknown, istanti: Istanti): VerdettoOrologio {
  const s = oggetto(state);
  if (!s) return { tipo: 'ignoto' };
  const clocks = oggetto(s.clocks);
  if (!clocks) return { tipo: 'ignoto' };
  if (clocks.unlimited === true) return { tipo: 'senza' };
  const { W, B } = clocks;
  if (typeof W !== 'number' || typeof B !== 'number') return { tipo: 'ignoto' };
  if (!Number.isFinite(W) || !Number.isFinite(B)) return { tipo: 'ignoto' };

  const scorre = latoChePensa(state);
  // ⚠️ `Math.max(0, …)` sul trascorso: un `adesso` precedente a `ricevutoA` —
  // orologio di sistema che torna indietro, chiamante che scambia i due
  // parametri — non deve poter REGALARE tempo. Fermo e' l'errore innocuo.
  const trascorso = Math.max(0, istanti.adesso - istanti.ricevutoA);
  return {
    tipo: 'orologio',
    orologio: {
      W: lato(W, scorre === 'W', trascorso),
      B: lato(B, scorre === 'B', trascorso),
      scorre,
    },
  };
}

function lato(grezzo: number, scorre: boolean, trascorso: number): LatoOrologio {
  const ms = Math.max(0, scorre ? grezzo - trascorso : grezzo);
  return {
    ms,
    testo: formattaTempo(ms),
    corto: sottoIlMinuto(ms),
    scorre,
    aZero: ms === 0,
  };
}

/**
 * La comodita' `| null` — cioe' la sola domanda «devo disegnare un orologio?».
 *
 * ⛔ Chi deve anche SCRIVERE qualcosa usa `verdettoOrologio`: qui `'senza'` e
 * `'ignoto'` tornano lo stesso `null`, e sono due frasi diverse.
 */
export function orologioDa(state: unknown, istanti: Istanti): Orologio | null {
  const v = verdettoOrologio(state, istanti);
  return v.tipo === 'orologio' ? v.orologio : null;
}

/**
 * I due lati dal punto di vista di chi guarda.
 *
 * ⚠️ Serve a scegliere **dove** mettere i due numeri (il proprio in basso, come
 * su ogni tabellone), non a etichettarne uno «il tuo tempo» su un modulo da
 * compilare: quel divieto vale per i punteggi di Limulo, non per un orologio,
 * che non si digita. Qui il lato lo sa gia' la schermata (`perspective`).
 */
export function perTe(
  orologio: Orologio,
  io: LatoQgammon,
): { mio: LatoOrologio; avversario: LatoOrologio } {
  return { mio: orologio[io], avversario: orologio[altroLato(io)] };
}
